/**
 * Main Page - Predictions table and filters
 * Shows paginated predictions with filters and tag report
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';
import { useAppStore } from '../store/appStore';
import { predictionsApi } from '../services/predictions.service';
import { useNotificationStore } from '../store/notificationStore';
import { PredictionFilters } from '../types';

// Components
import AppLayout from '../components/AppLayout';
import PredictionsFilters from '../components/PredictionsFilters';
import PredictionsTable from '../components/PredictionsTable';
import TagReport from '../components/TagReport';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function MainPage() {
  const selectedProduct = useAppStore((state) => state.selectedProduct);
  const selectedExperiment = useAppStore((state) => state.selectedExperiment);
  const { showNotification } = useNotificationStore();

  // State management
  const [currentTab, setCurrentTab] = useState(0);

  // Download/Export state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadCount, setDownloadCount] = useState<number | null>(null);
  const [isCountingRecords, setIsCountingRecords] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [filters, setFilters] = useState<PredictionFilters>(() => {
    const savedFilters = localStorage.getItem('predictions-filters');
    return savedFilters ? JSON.parse(savedFilters) : {};
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageHistory, setPageHistory] = useState<Array<Record<string, any> | null>>([null]);
  const [pageSize, setPageSize] = useState(() => {
    const savedPageSize = localStorage.getItem('predictions-page-size');
    return savedPageSize ? Number(savedPageSize) : 25;
  });

  // Page size options
  const pageSizeOptions = [25, 50, 100, 250, 500, 1000];

  // Save pageSize to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('predictions-page-size', pageSize.toString());
  }, [pageSize]);

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem('predictions-filters', JSON.stringify(filters));
  }, [filters]);

  // Reset pagination when filters or page size change
  useEffect(() => {
    setCurrentPage(1);
    setPageHistory([null]);
  }, [filters, pageSize, selectedProduct, selectedExperiment]);

  // Fetch filter values
  const { data: filterValues } = useQuery({
    queryKey: ['filter-values', selectedProduct, selectedExperiment],
    queryFn: () => predictionsApi.getFilterValues(selectedProduct, selectedExperiment),
  });

  // Fetch S3 prediction IDs (to show data availability indicator)
  const { data: s3PredictionIds = [] } = useQuery({
    queryKey: ['s3-prediction-ids', selectedProduct, selectedExperiment],
    queryFn: () => predictionsApi.getS3PredictionIds(selectedProduct, selectedExperiment),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch predictions
  const {
    data: predictionsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'predictions',
      selectedProduct,
      selectedExperiment,
      filters,
      currentPage,
      pageSize,
      pageHistory[currentPage - 1],
    ],
    queryFn: () =>
      predictionsApi.list(selectedProduct, selectedExperiment, {
        filters,
        page: currentPage,
        pageSize,
        lastEvaluatedKey: pageHistory[currentPage - 1] || undefined,
      }),
    placeholderData: (previousData) => previousData,
  });

  const predictions = predictionsData?.items || [];
  const pagination = useMemo(
    () => ({
      currentPage,
      hasNextPage: predictionsData?.pagination.hasNextPage || false,
      hasPreviousPage: currentPage > 1,
    }),
    [currentPage, predictionsData]
  );

  const handlePageChange = (direction: 'next' | 'prev') => {
    if (direction === 'next' && pagination.hasNextPage) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      // Store the lastEvaluatedKey for next page
      if (predictionsData?.pagination.lastEvaluatedKey) {
        setPageHistory((prev) => {
          const newHistory = [...prev];
          newHistory[newPage - 1] = predictionsData.pagination.lastEvaluatedKey || null;
          return newHistory;
        });
      }
    } else if (direction === 'prev' && pagination.hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const availableFilters = useMemo(
    () => ({
      grades: filterValues?.grades || [],
      units: filterValues?.units || [],
      reviewers: filterValues?.reviewers || [],
      assignees: filterValues?.assignees || [],
    }),
    [filterValues]
  );

  // Check if any additional filters are active (beyond product/experiment)
  const hasAdditionalFilters = useMemo(() => {
    return Object.entries(filters).some(([_key, value]) => {
      if (!value) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.trim().length > 0;
      return false;
    });
  }, [filters]);

  // Handle download button click - show confirmation modal
  const handleDownloadClick = async () => {
    setShowDownloadModal(true);
    setDownloadCount(null);
    setIsCountingRecords(true);

    try {
      const count = await predictionsApi.getExportCount(
        selectedProduct,
        selectedExperiment,
        filters
      );
      setDownloadCount(count);
    } catch (error: any) {
      showNotification('error', `Failed to count records: ${error.message}`);
      setShowDownloadModal(false);
    } finally {
      setIsCountingRecords(false);
    }
  };

  // Handle confirmed download
  const handleConfirmDownload = async () => {
    setIsDownloading(true);

    try {
      const { blob, filename } = await predictionsApi.exportCSV(
        selectedProduct,
        selectedExperiment,
        filters
      );

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification('success', `Downloaded ${downloadCount?.toLocaleString()} records`);
      setShowDownloadModal(false);
    } catch (error: any) {
      showNotification('error', `Download failed: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCancelDownload = () => {
    setShowDownloadModal(false);
    setDownloadCount(null);
  };

  return (
    <AppLayout>
      <Container maxWidth={false} sx={{ py: 3, px: 3, maxWidth: '1800px' }}>
        {/* Page Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0.5 }}>
            Predictions
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs 
            value={currentTab} 
            onChange={(_, newValue) => setCurrentTab(newValue)}
            aria-label="Predictions tabs"
          >
            <Tab label="Predictions Table" />
            <Tab label="Tag Report" />
          </Tabs>
        </Box>

        {/* Predictions Table Tab */}
        <TabPanel value={currentTab} index={0}>
          {/* Filters */}
          <Box sx={{ mb: 2 }}>
            <PredictionsFilters
              filters={filters}
              onFiltersChange={setFilters}
              availableFilters={availableFilters}
              onDownload={handleDownloadClick}
              isDownloading={isDownloading}
            />
          </Box>

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load predictions. Please try again.
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Table */}
          {!isLoading && !error && (
            <>
              <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {predictionsData?.pagination.hasNextPage === false && predictionsData?.pagination.hasPreviousPage === false && Object.keys(filters).some(key => {
                    const filterValue = filters[key as keyof PredictionFilters];
                    return filterValue && (Array.isArray(filterValue) ? filterValue.length > 0 : filterValue);
                  })
                    ? `Showing ${predictions.length} filtered results`
                    : `Showing ${predictions.length} predictions (Page ${currentPage})`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Records per page</InputLabel>
                    <Select
                      value={pageSize}
                      label="Records per page"
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      {pageSizeOptions.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <PredictionsTable
                predictions={predictions}
                sorting={sorting}
                onSortingChange={setSorting}
                pagination={pagination}
                onPageChange={handlePageChange}
                product={selectedProduct}
                experiment={selectedExperiment}
                availableAssignees={availableFilters.assignees}
                s3PredictionIds={s3PredictionIds}
              />
            </>
          )}
        </TabPanel>

        {/* Tag Report Tab */}
        <TabPanel value={currentTab} index={1}>
          {selectedProduct && selectedExperiment ? (
            <TagReport product={selectedProduct} experiment={selectedExperiment} />
          ) : (
            <Alert severity="warning">
              Please select a product and experiment version to view the tag report.
            </Alert>
          )}
        </TabPanel>
      </Container>

      {/* Download Confirmation Modal */}
      <Dialog open={showDownloadModal} onClose={handleCancelDownload} maxWidth="sm" fullWidth>
        <DialogTitle>Export Predictions</DialogTitle>
        <DialogContent>
          {isCountingRecords ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography>Counting records...</Typography>
            </Box>
          ) : isDownloading ? (
            <Box sx={{ py: 3 }}>
              <Typography sx={{ mb: 2 }}>
                Exporting {downloadCount?.toLocaleString()} records...
              </Typography>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                This may take a moment for large exports.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 1 }}>
              {!hasAdditionalFilters && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  No filters selected. This will export ALL records for {selectedProduct} / {selectedExperiment}.
                </Alert>
              )}
              <Typography>
                This will download <strong>{downloadCount?.toLocaleString()}</strong> records as a CSV file.
              </Typography>
              {hasAdditionalFilters && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Export includes your current filter settings.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDownload} disabled={isDownloading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDownload}
            variant="contained"
            disabled={isCountingRecords || isDownloading || downloadCount === 0}
          >
            {isDownloading ? 'Exporting...' : 'Download CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}

